<script lang="ts">
  import { onDestroy } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Chart from 'chart.js/auto';
  import Header from '../../app/layout/Header.svelte';
  import { resolvedTheme } from '../../app/theme/theme';
  import { listCategories } from '../../data/categories';
  import { sumExpensesByMonthGroupedByCategory } from '../../data/expenses';
  import type { Category } from '../../data/types';
  import { getCurrentYearMonth, getPreviousYearMonth, getNextYearMonth } from '../../lib/date';
  import { formatCurrencyJPY } from '../../lib/format';

  interface CategoryAmount {
    category: Category;
    amount: number;
    percent: number;
  }

  function formatYearMonthJP(yearMonth: string): string {
    const [year, month] = yearMonth.split('-');
    return `${year}年${Number(month)}月`;
  }

  let yearMonth = $state(getCurrentYearMonth());
  let loading = $state(true);
  let errorMessage = $state('');
  let hasCategories = $state(true);
  let rawItems = $state<{ category: Category; amount: number }[]>([]);

  const total = $derived(rawItems.reduce((sum, item) => sum + item.amount, 0));
  const items = $derived<CategoryAmount[]>(
    rawItems.map((item) => ({
      ...item,
      percent: total > 0 ? (item.amount / total) * 100 : 0,
    }))
  );
  const chartItems = $derived(items.filter((item) => item.amount > 0));
  const isCurrentMonth = $derived(yearMonth === getCurrentYearMonth());

  async function loadData(month: string) {
    loading = true;
    errorMessage = '';
    try {
      const [categories, sums] = await Promise.all([
        listCategories(),
        sumExpensesByMonthGroupedByCategory(month),
      ]);
      hasCategories = categories.length > 0;
      const merged = categories.map((category) => ({
        category,
        amount: sums[category.id] ?? 0,
      }));
      merged.sort((a, b) => b.amount - a.amount);
      rawItems = merged;
    } catch (err) {
      console.error(err);
      errorMessage = 'データの読み込みに失敗しました。';
      rawItems = [];
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    loadData(yearMonth);
  });

  function goPrevMonth() {
    yearMonth = getPreviousYearMonth(yearMonth);
  }

  function goNextMonth() {
    yearMonth = getNextYearMonth(yearMonth);
  }

  function goCurrentMonth() {
    yearMonth = getCurrentYearMonth();
  }

  function handleMonthInput(event: Event) {
    const value = (event.currentTarget as HTMLInputElement).value;
    if (/^\d{4}-\d{2}$/.test(value)) {
      yearMonth = value;
    }
  }

  // --- Chart.js 描画 ---
  let canvasEl: HTMLCanvasElement | undefined = $state();
  let chart: Chart | undefined;

  function themeColor(varName: string): string {
    if (typeof window === 'undefined') return '#000000';
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  }

  function renderChart(data: CategoryAmount[]) {
    if (!canvasEl) return;

    if (chart) {
      chart.destroy();
      chart = undefined;
    }

    if (data.length === 0) return;

    const primaryColor = themeColor('--color-primary');
    const textMuted = themeColor('--color-text-muted');
    const borderColor = themeColor('--color-border');
    const bgElevated = themeColor('--color-bg-elevated');
    const textColor = themeColor('--color-text');

    chart = new Chart(canvasEl, {
      type: 'bar',
      data: {
        labels: data.map((item) => item.category.name),
        datasets: [
          {
            label: '支出合計',
            data: data.map((item) => item.amount),
            backgroundColor: primaryColor,
            borderRadius: 4,
            borderSkipped: false,
            maxBarThickness: 24,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 200 },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: bgElevated,
            titleColor: textColor,
            bodyColor: textColor,
            borderColor: borderColor,
            borderWidth: 1,
            padding: 8,
            displayColors: false,
            callbacks: {
              label: (ctx) => formatCurrencyJPY(Number(ctx.raw ?? 0)),
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: borderColor },
            border: { color: borderColor },
            ticks: {
              color: textMuted,
              callback: (value) => formatCurrencyJPY(Number(value)),
            },
          },
          y: {
            grid: { display: false },
            border: { color: borderColor },
            ticks: { color: textMuted },
          },
        },
      },
    });
  }

  $effect(() => {
    // chartItems・canvas要素・テーマ（ライト/ダーク）が変わるたびに再描画する
    const data = chartItems;
    const el = canvasEl;
    const themeTick = $resolvedTheme;
    void themeTick;
    if (el) {
      renderChart(data);
    }
  });

  onDestroy(() => {
    chart?.destroy();
  });
</script>

<Header title="集計" showBack={true} backTo="/" />

<main class="page">
  <section class="month-selector">
    <button
      type="button"
      class="icon-button"
      onclick={goPrevMonth}
      aria-label="前月へ"
    >
      ←
    </button>
    <label class="month-input-label">
      <span class="visually-hidden">対象月</span>
      <input
        type="month"
        class="month-input"
        value={yearMonth}
        onchange={handleMonthInput}
      />
    </label>
    <button
      type="button"
      class="icon-button"
      onclick={goNextMonth}
      aria-label="翌月へ"
    >
      →
    </button>
    {#if !isCurrentMonth}
      <button type="button" class="nav-button" onclick={goCurrentMonth}>今月</button>
    {/if}
  </section>

  <section class="total-card">
    <span class="total-card__label">{formatYearMonthJP(yearMonth)}の支出合計</span>
    <span class="total-card__value">{formatCurrencyJPY(total)}</span>
  </section>

  {#if errorMessage}
    <p class="error-text">{errorMessage}</p>
  {/if}

  {#if loading}
    <p class="muted-text">読み込み中...</p>
  {:else if !hasCategories}
    <p class="muted-text">
      カテゴリが登録されていません。「カテゴリ管理」から追加してください。
    </p>
    <button type="button" class="nav-button" onclick={() => push('/category-mgmt')}>
      カテゴリ管理へ
    </button>
  {:else if chartItems.length === 0}
    <p class="muted-text">この月の支出データはありません。</p>
  {:else}
    <section class="chart-card">
      <div
        class="chart-container"
        style={`height: ${Math.max(180, chartItems.length * 40)}px`}
      >
        <canvas bind:this={canvasEl}></canvas>
      </div>
    </section>
  {/if}

  {#if !loading && hasCategories && items.length > 0}
    <ul class="category-list">
      {#each items as item (item.category.id)}
        <li class="category-row">
          <div class="category-row__top">
            <span class="category-row__name">{item.category.name}</span>
            <span class="category-row__amount">{formatCurrencyJPY(item.amount)}</span>
          </div>
          <div class="row-bar">
            <div class="row-bar__fill" style={`width: ${item.percent}%`}></div>
          </div>
          <span class="category-row__percent">{item.percent.toFixed(1)}%</span>
        </li>
      {/each}
    </ul>
  {/if}
</main>

<style>
  .page {
    padding: 1rem;
    max-width: 640px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .month-selector {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    color: var(--color-text);
    cursor: pointer;
    font-size: 1rem;
  }

  .icon-button:hover,
  .icon-button:focus-visible {
    border-color: var(--color-primary);
    outline: none;
  }

  .month-input-label {
    display: inline-flex;
  }

  .month-input {
    padding: 0.45rem 0.6rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    color: var(--color-text);
    font-size: 0.95rem;
  }

  .nav-button {
    padding: 0.5rem 0.9rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    color: var(--color-text);
    cursor: pointer;
    font-size: 0.9rem;
  }

  .nav-button:hover,
  .nav-button:focus-visible {
    border-color: var(--color-primary);
    outline: none;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .total-card {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 1rem 1.1rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    box-shadow: 0 1px 2px var(--color-shadow);
  }

  .total-card__label {
    font-size: 0.8rem;
    color: var(--color-text-muted);
  }

  .total-card__value {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--color-text);
  }

  .muted-text {
    color: var(--color-text-muted);
  }

  .error-text {
    color: var(--color-danger);
  }

  .chart-card {
    padding: 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    box-shadow: 0 1px 2px var(--color-shadow);
  }

  .chart-container {
    position: relative;
    width: 100%;
  }

  .category-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .category-row {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 0.75rem 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
  }

  .category-row__top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .category-row__name {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .category-row__amount {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-text);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .row-bar {
    height: 6px;
    border-radius: var(--radius-sm);
    background-color: var(--color-border);
    overflow: hidden;
  }

  .row-bar__fill {
    height: 100%;
    border-radius: inherit;
    background-color: var(--color-primary);
  }

  .category-row__percent {
    font-size: 0.75rem;
    color: var(--color-text-muted);
    font-variant-numeric: tabular-nums;
  }
</style>
